import { I18nService } from 'core-app/core/i18n/i18n.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import {
  SearchableProjectListService,
} from 'core-app/shared/components/searchable-project-list/searchable-project-list.service';
import { IProjectData } from 'core-app/shared/components/searchable-project-list/project-data';
import { PathHelperService } from 'core-app/core/path-helper/path-helper.service';
import { ConfigurationService } from 'core-app/core/config/configuration.service';
import { CurrentProjectService } from 'core-app/core/current-project/current-project.service';
import { getMetaContent } from 'core-app/core/setup/globals/global-helpers';

@Component({
  selector: '[op-header-project-select-list]',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './header-project-select-list.component.html',
  styleUrls: ['./header-project-select-list.component.sass'],
  standalone: false,
})
export class OpHeaderProjectSelectListComponent implements OnInit, OnChanges {
  readonly I18n = inject(I18nService);
  readonly pathHelper = inject(PathHelperService);
  readonly configuration = inject(ConfigurationService);
  readonly searchableProjectListService = inject(SearchableProjectListService);
  readonly elementRef = inject(ElementRef);
  readonly cdRef = inject(ChangeDetectorRef);
  readonly currentProjectService = inject(CurrentProjectService);

  @HostBinding('class.spot-list') classNameList = true;

  @HostBinding('class.op-header-project-select-list') className = true;

  @HostBinding('attr.role')
  get roleAttribute():string {
    return this.root ? 'listbox' : 'group';
  }

  @HostBinding('attr.id')
  get idAttribute():string|null {
    return this.root ? 'op-header-project-select-listbox' : null;
  }

  @Output() update = new EventEmitter<string[]>();

  @Input() @HostBinding('class.op-header-project-select-list--root') root = false;

  @Input() projects:IProjectData[] = [];

  @Input() favorited:string[] = [];

  @Input() displayMode:string;

  @Input() searchText = '';

  public filteredProjects:IProjectData[];

  public text = {
    does_not_match_search: this.I18n.t('js.include_projects.tooltip.does_not_match_search'),
    include_all_selected: this.I18n.t('js.include_projects.tooltip.include_all_selected')
  };

  public portfolioModelsEnabled = this.configuration.activeFeatureFlags.includes('portfolioModels');

  ngOnInit():void {
    if (this.root) {
      this.searchableProjectListService.selectedItemID$.subscribe((selectedItemID) => {
        // We have to push this back once so the component gets time to render the list
        // and we can actually find the element and scroll to it.
        requestAnimationFrame(() => {
          const itemAction = (this.elementRef.nativeElement as HTMLElement)
            .querySelectorAll(`.spot-list--item-action[data-project-id="${selectedItemID ?? ''}"]`);
          itemAction[0]?.scrollIntoView();
        });
      });
    }

    this.updateProjectFilter();
  }

  ngOnChanges(changes:SimpleChanges) {
    if (changes.displayMode || changes.projects || changes.favorited) {
      this.updateProjectFilter();
    }
  }

  updateProjectFilter() {
    this.filteredProjects = this.projects.filter((project) => {
      if (this.displayMode === 'all') {
        return true;
      }

      return this.showWhenFavorited(project);
    });
  }

  showWhenFavorited(project:IProjectData):boolean {
    if (this.isFavorited(project)) {
      return true;
    }

    return project.children.length > 0 && project.children.some((child) => this.showWhenFavorited(child));
  }

  isFavorited(project:IProjectData):boolean {
    return this.favorited.includes(project.id.toString());
  }

  extendedUrl(projectId:string|null):string {
    const currentMenuItem = getMetaContent('current_menu_item');
    const url = projectId === null ? window.appBasePath : this.pathHelper.projectPath(projectId);

    if (!currentMenuItem) {
      return url;
    }

    return `${url}?jump=${encodeURIComponent(currentMenuItem)}`;
  }

  optionId(project:IProjectData):string {
    return `op-header-project-select-option-${project.id}`;
  }
}
